(function () {
  if (window.__dyRemoteProductCardLoaded) {
    return;
  }

  window.__dyRemoteProductCardLoaded = true;

  class RemoteProductCard extends HTMLElement {
    constructor() {
      super();
      this.sectionEndpoint =
        this.getAttribute("section-id") || "dy-remote-product-card";
      this.requestToken = null;
      this.lastLoadedKey = null;
      this.cacheTtl = 3 * 60 * 1000;
      this.maxCacheEntries = 30;
    }

    static get observedAttributes() {
      return ["handle", "variant-id"];
    }

    connectedCallback() {
      if (!this.children.length) {
        this.dataset.dyState = "loading";
      }
      this.loadCard();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.isConnected || oldValue === newValue) {
        return;
      }

      if (name === "handle" || name === "variant-id") {
        this.loadCard();
      }
    }

    get handle() {
      return this.getAttribute("handle");
    }

    get variantId() {
      return this.getAttribute("variant-id") || this.getAttribute("variantId");
    }

    buildUrl() {
      var rootPath =
        window.Shopify && Shopify.routes && Shopify.routes.root
          ? Shopify.routes.root
          : "/";

      if (rootPath.slice(-1) !== "/") {
        rootPath += "/";
      }

      var url =
        rootPath +
        "products/" +
        encodeURIComponent(this.handle) +
        "?variant=" +
        encodeURIComponent(this.variantId || "") +
        "&section_id=" +
        encodeURIComponent(this.sectionEndpoint);

      return url;
    }

    getCacheStore() {
      if (!window.__dyRemoteProductCardCache) {
        window.__dyRemoteProductCardCache = new Map();
      }
      return window.__dyRemoteProductCardCache;
    }

    getInFlightStore() {
      if (!window.__dyRemoteProductCardInFlight) {
        window.__dyRemoteProductCardInFlight = new Map();
      }
      return window.__dyRemoteProductCardInFlight;
    }

    extractRenderedMarkup(text) {
      var html = new DOMParser().parseFromString(text, "text/html");
      var sectionWrapper = html.querySelector('[id^="shopify-section-"]');

      if (sectionWrapper) {
        return sectionWrapper.innerHTML;
      }

      if (html.body) {
        return html.body.innerHTML;
      }

      return text;
    }

    setCachedMarkup(cacheKey, markup) {
      if (!markup) {
        return;
      }

      var cacheStore = this.getCacheStore();
      var now = Date.now();

      cacheStore.forEach((entry, key) => {
        if (!entry || entry.expiresAt < now) {
          cacheStore.delete(key);
        }
      });

      cacheStore.set(cacheKey, {
        markup: markup,
        expiresAt: now + this.cacheTtl,
      });

      if (cacheStore.size > this.maxCacheEntries) {
        cacheStore.delete(cacheStore.keys().next().value);
      }
    }

    getCachedMarkup(cacheKey) {
      var cacheStore = this.getCacheStore();
      var cached = cacheStore.get(cacheKey);

      if (!cached) {
        return null;
      }

      if (cached.expiresAt < Date.now()) {
        cacheStore.delete(cacheKey);
        return null;
      }

      return cached.markup;
    }

    renderCardMarkup(markup, requestKey) {
      if (!this.isConnected) {
        return;
      }

      this.innerHTML = markup;
      this.dataset.dyState = "ready";
      this.applyVariantToLink();
      this.lastLoadedKey = requestKey;

      this.dispatchEvent(
        new CustomEvent("remote-product-card:loaded", {
          bubbles: true,
          detail: {
            handle: this.handle,
            variantId: this.variantId || null,
          },
        })
      );
    }

    applyVariantToLink() {
      if (!this.variantId) {
        return;
      }

      var productLink = this.querySelector(".grid-product__link");
      if (!productLink) {
        return;
      }

      try {
        var nextUrl = new URL(productLink.getAttribute("href"), window.location.origin);
        nextUrl.searchParams.set("variant", this.variantId);
        productLink.setAttribute(
          "href",
          nextUrl.pathname + nextUrl.search + nextUrl.hash
        );
      } catch (error) {
        console.error(error);
      }
    }

    loadCard() {
      if (!this.handle) {
        return;
      }

      var requestKey = this.handle + "::" + (this.variantId || "");
      if (this.lastLoadedKey === requestKey && this.children.length) {
        return;
      }

      var requestUrl = this.buildUrl();
      // Opt-in freshness: cards that must reflect current settings (e.g. the
      // upsell modal) set `data-fresh` to bypass the in-memory + HTTP cache.
      var fresh = this.hasAttribute("data-fresh");
      var inFlightKey = fresh ? requestUrl + "::fresh" : requestUrl;

      if (!fresh) {
        var cachedMarkup = this.getCachedMarkup(requestUrl);
        if (cachedMarkup) {
          this.renderCardMarkup(cachedMarkup, requestKey);
          return;
        }
      }

      var token = Date.now() + "-" + Math.random();
      this.requestToken = token;
      var inFlightStore = this.getInFlightStore();
      var inFlightRequest = inFlightStore.get(inFlightKey);

      if (!inFlightRequest) {
        inFlightRequest = fetch(requestUrl, {
          credentials: "same-origin",
          cache: fresh ? "reload" : "force-cache",
        })
          .then((response) => response.text())
          .then((text) => {
            var renderedMarkup = this.extractRenderedMarkup(text);
            if (!fresh) {
              this.setCachedMarkup(requestUrl, renderedMarkup);
            }
            inFlightStore.delete(inFlightKey);
            return renderedMarkup;
          })
          .catch((error) => {
            inFlightStore.delete(inFlightKey);
            throw error;
          });

        inFlightStore.set(inFlightKey, inFlightRequest);
      }

      inFlightRequest
        .then((renderedMarkup) => {
          if (this.requestToken !== token) {
            return;
          }

          this.renderCardMarkup(renderedMarkup, requestKey);
        })
        .catch((error) => {
          this.dataset.dyState = "error";
          console.error(error);
        });
    }
  }

  if (!customElements.get("remote-product-card")) {
    customElements.define("remote-product-card", RemoteProductCard);
  }
})();
